import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useDiscrepancySummary, useDetectGaps } from "@/hooks/use-monitoring-gaps";
import { DiscrepancySummaryTable } from "@/components/schedules/discrepancies-summary-table";
import { ParameterPeriodMatrix } from "@/components/schedules/parameter-period-matrix";
import { GapActionPanel } from "@/components/schedules/gap-action-panel";
import { ExpectedSubmissionsTable } from "@/components/schedules/expected-submissions-table";
import { ScheduleAdherenceTab } from "@/components/schedules/schedule-adherence-tab";
import { ScheduleAdherenceSummaryList } from "@/components/schedules/schedule-adherence-summary";
import {
  PageHeader,
  Button,
  EmptyState,
  SkeletonTable,
  StatCard,
  Breadcrumbs,
} from "@/components/ui";

type DiscrepancySummary = components["schemas"]["DiscrepancySummary"];
type MonitoringGap = components["schemas"]["MonitoringGap"];

type TabId = "summary" | "schedule-adherence" | "submissions";

export const Route = createFileRoute("/_authenticated/monitoring-gaps/")({
  component: MonitoringGapsPage,
});

function MonitoringGapsPage() {
  const { t } = useTranslation("schedules");
  const orgId = useAppStore((s) => s.activeOrgId);

  const [activeTab, setActiveTab] = useState<TabId>("summary");
  const [selectedPermitId, setSelectedPermitId] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [selectedGaps, setSelectedGaps] = useState<MonitoringGap[]>([]);

  const { data: summaryData, isLoading, isFetching } = useDiscrepancySummary(orgId);
  const detectGaps = useDetectGaps(orgId);

  const summaries = summaryData?.summaries ?? [];
  const permitCompletenessPct = summaryData?.permit_completeness_pct;
  const scheduleAdherencePct = summaryData?.schedule_adherence_pct;

  const handleDetect = () => {
    detectGaps.mutate(undefined);
  };

  const handleRowClick = (row: DiscrepancySummary) => {
    setSelectedPermitId(row.permit_id);
  };

  if (selectedPermitId) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            { label: t("monitoringGaps.title"), to: "/monitoring-gaps" },
            { label: t("monitoringGaps.matrix.title") },
          ]}
        />

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setSelectedPermitId(null)}
            className="text-sm font-medium text-primary hover:underline"
          >
            &larr; {t("monitoringGaps.matrix.backToSummary")}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className={selectedGaps.length > 0 ? "lg:col-span-2" : "lg:col-span-3"}>
            <ParameterPeriodMatrix
              orgId={orgId ?? ""}
              permitId={selectedPermitId}
              onCellClick={setSelectedGaps}
            />
          </div>
          {selectedGaps.length > 0 && (
            <div className="lg:col-span-1">
              <GapActionPanel gaps={selectedGaps} onClose={() => setSelectedGaps([])} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("monitoringGaps.title") }]} />

      <PageHeader
        title={t("monitoringGaps.title")}
        actions={
          <Button onClick={handleDetect} disabled={detectGaps.isPending}>
            {detectGaps.isPending
              ? t("monitoringGaps.summary.running")
              : t("monitoringGaps.summary.runDetection")}
          </Button>
        }
      />

      {detectGaps.isSuccess && detectGaps.data && (
        <div className="mt-4 rounded-md bg-green-50 px-4 py-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
          {t("monitoringGaps.summary.detectionComplete", {
            gaps: detectGaps.data.gaps_detected,
            new: detectGaps.data.new_gaps_created,
          })}
        </div>
      )}

      {/* Summary stat cards */}
      {!isLoading && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title={t("monitoringGaps.permitCompletenessPct")}
            value={
              permitCompletenessPct !== undefined ? `${Math.round(permitCompletenessPct)}%` : "—"
            }
            loading={isLoading}
          />
          <StatCard
            title={t("monitoringGaps.scheduleAdherencePct")}
            value={
              scheduleAdherencePct !== undefined ? `${Math.round(scheduleAdherencePct)}%` : "—"
            }
            loading={isLoading}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6" aria-label="Tabs">
          <TabButton
            active={activeTab === "summary"}
            onClick={() => setActiveTab("summary")}
            label={t("monitoringGaps.summary.title")}
          />
          <TabButton
            active={activeTab === "schedule-adherence"}
            onClick={() => setActiveTab("schedule-adherence")}
            label={t("monitoringGaps.scheduleAdherenceTab")}
          />
          <TabButton
            active={activeTab === "submissions"}
            onClick={() => setActiveTab("submissions")}
            label={t("monitoringGaps.expectedSubmissions.tabLabel")}
          />
        </nav>
      </div>

      <div className="mt-4">
        {activeTab === "summary" && (
          <>
            {isLoading ? (
              <SkeletonTable columns={6} />
            ) : summaries.length === 0 && !isFetching ? (
              <EmptyState
                title={t("monitoringGaps.summary.noPermits")}
                description={t("monitoringGaps.summary.noPermitsDescription")}
              />
            ) : (
              <DiscrepancySummaryTable
                summaries={summaries}
                onRowClick={handleRowClick}
                isLoading={isFetching}
              />
            )}
          </>
        )}

        {activeTab === "schedule-adherence" &&
          (selectedScheduleId ? (
            <div>
              <button
                type="button"
                onClick={() => setSelectedScheduleId(null)}
                className="mb-4 text-sm font-medium text-primary hover:underline"
              >
                &larr; {t("monitoringGaps.scheduleAdherenceSummary.backToList")}
              </button>
              <ScheduleAdherenceTab orgId={orgId ?? ""} scheduleId={selectedScheduleId} />
            </div>
          ) : (
            <ScheduleAdherenceSummaryList
              orgId={orgId ?? ""}
              onScheduleClick={(id) => setSelectedScheduleId(id)}
            />
          ))}

        {activeTab === "submissions" && <ExpectedSubmissionsTable />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 pb-2 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted hover:text-on-surface"
      }`}
    >
      {label}
    </button>
  );
}
